import React, {useState} from 'react';

import {Button, Input, Label, Text} from '@albert-io/atomic';

import OnboardingPageLayout from '../OnboardingPageLayout';
import type {PersonalInfoData} from '../onboarding.types';
import UsernameSuggestions from '../UsernameSuggestions';
import {useUsernameSuggestions} from '../useUsernameSuggestions';

interface ConfirmInfoStepProps {
  onSubmit: (data: PersonalInfoData) => void;
  isSubmitting?: boolean;
  usernameError?: string | null;
  /** Pre-fills the input — populated when SSO (Clever/Google) provides the name. */
  defaultFirstName?: string;
  /** Pre-fills the input — populated when SSO (Clever/Google) provides the name. */
  defaultLastName?: string;
}

export default function ConfirmInfoStep({
  onSubmit,
  isSubmitting,
  usernameError,
  defaultFirstName = '',
  defaultLastName = ''
}: ConfirmInfoStepProps) {
  const [firstName, setFirstName] = useState(defaultFirstName);
  const [lastName, setLastName] = useState(defaultLastName);
  const [username, setUsername] = useState('');

  const {suggestions, isFormatValid, isUsernameTaken, validationError} = useUsernameSuggestions({
    firstName,
    lastName,
    username
  });

  const displayError = usernameError ?? validationError;
  const isValid = firstName.trim() && lastName.trim() && isFormatValid && !isUsernameTaken;

  return (
    <OnboardingPageLayout>
      <Text className='text-xxl font-black'>Confirm your information</Text>

      <div className='flex flex-col gap-x3 px-x1'>
        <div className='flex flex-col gap-x1-half m:flex-row m:gap-x3'>
          <div className='flex-1'>
            <Label>First Name</Label>
            <Input
              placeholder='e.g. Albert'
              className='mt-x1'
              value={firstName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
            />
          </div>
          <div className='flex-1'>
            <Label>Last Name</Label>
            <Input
              className='mt-x1'
              placeholder='e.g. Smith'
              value={lastName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label>Username</Label>
          <Input
            className='mt-x1'
            placeholder='e.g. albertsmith'
            value={username}
            error={!!displayError}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
          />
          {displayError && (
            <Text size='s' color='negative' className='mt-x0-half'>
              {displayError}
            </Text>
          )}
          <UsernameSuggestions
            suggestions={suggestions}
            selectedUsername={username}
            onSelect={setUsername}
          />
        </div>
      </div>

      <Button
        className='w-full'
        color='primary'
        size='l'
        roundness='pill'
        disabled={!isValid || isSubmitting}
        onClick={() => onSubmit({firstName, lastName, username})}
      >
        <Text color='primary-inverse-static'>{isSubmitting ? 'Saving...' : 'Submit'}</Text>
      </Button>
    </OnboardingPageLayout>
  );
}
